import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, Typography, Chip, IconButton, Box, Button, Modal, Slider } from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { formatDate, getDocumentBlob } from '../../../../utils';
import { useNavigate } from 'react-router-dom';
import { sharedDocumentsMapping } from '../../../../pages/Organisation/SharedDocumentsRequests/utils';

const UserDocumentRequest = ({
	status,
	sharedDocumentRequests,
	_onOpen // renamed to indicate it's unused
}: {
	status: string;
	sharedDocumentRequests: any; // Using any since we're receiving direct API response
	_onOpen: () => void; // renamed to indicate it's unused
}) => {
	const navigate = useNavigate();
	const imageContainerRef = useRef<HTMLDivElement>(null);
	const [isMobile, setIsMobile] = useState(false);

	// State for tracking current request and step
	const [currentRequestIndex, setCurrentRequestIndex] = useState(0);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [blobContent, setBlobContent] = useState<string | Blob>('');
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1);
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

	// Get requests array from the response
	const requests = Array.isArray(sharedDocumentRequests) ? sharedDocumentRequests : [];

	useEffect(() => {
		const checkScreenSize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => {
			window.removeEventListener('resize', checkScreenSize);
		};
	}, []);

	// Get the current request
	const currentRequest = requests[currentRequestIndex] || null;

	// Get steps from current request
	const steps = currentRequest?.steps || [];
	const currentStep = steps[currentStepIndex] || null;

	useEffect(() => {
		const loadDocument = async () => {
			const doc = sharedDocumentRequests[currentRequestIndex]?.documents?.find(
				(doc: any) =>
					doc.id === sharedDocumentRequests[currentRequestIndex]?.stepsData?.[currentStepIndex]?.documentId
			);
			if (!doc) {
				setBlobContent('');
				return;
			}
			try {
				const documentBlob = await getDocumentBlob(doc);
				const url = URL.createObjectURL(documentBlob || new Blob());
				setBlobContent(url);
			} catch (error) {
				console.error('Error loading document:', error);
				setBlobContent('');
			}
		};
		loadDocument();
	}, [currentRequestIndex, currentStepIndex]);

	// Handle request navigation
	const nextRequest = () => {
		if (currentRequestIndex < requests.length - 1) {
			setCurrentRequestIndex(currentRequestIndex + 1);
			setCurrentStepIndex(0); // Reset step index when changing requests
		}
	};

	const prevRequest = () => {
		if (currentRequestIndex > 0) {
			setCurrentRequestIndex(currentRequestIndex - 1);
			setCurrentStepIndex(0); // Reset step index when changing requests
		}
	};

	// Handle step navigation
	const nextStep = () => {
		if (currentStepIndex < steps.length - 1) {
			setCurrentStepIndex(currentStepIndex + 1);
		}
	};

	const prevStep = () => {
		if (currentStepIndex > 0) {
			setCurrentStepIndex(currentStepIndex - 1);
		}
	};

	// Handle opening the request
	const handleOpenRequest = () => {
		if (currentRequest && currentRequest.organisationId) {
			navigate(`/organisations/${currentRequest.organisationId}/shared-documents-requests/${currentRequest.id}`);
		}
	};

	// Toggle image modal
	const handleImageClick = () => {
		if (blobContent && blobContent !== '') {
			setImageModalOpen(true);
		}
	};

	const handleCloseModal = () => {
		setImageModalOpen(false);
		// Reset zoom level when closing the modal
		setZoomLevel(1);
		setDragOffset({ x: 0, y: 0 });
	};

	// Zoom controls
	const handleZoomIn = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation(); // Prevent modal closing
		setZoomLevel((prevZoom) => Math.min(prevZoom + 0.25, 5));
	};

	const handleZoomOut = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation(); // Prevent modal closing
		setZoomLevel((prevZoom) => Math.max(prevZoom - 0.25, 0.5));
	};

	const handleZoomReset = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setZoomLevel(1);
		setDragOffset({ x: 0, y: 0 });
	};

	const handleSliderChange = (_event: Event, newValue: number | number[]) => {
		setZoomLevel(newValue as number);
	};

	// Pan handlers for moving the image when zoomed in
	const handleMouseDown = (e: React.MouseEvent) => {
		if (zoomLevel > 1) {
			setIsDragging(true);
			setDragStart({ x: e.clientX, y: e.clientY });
		}
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isDragging) return;

		const deltaX = e.clientX - dragStart.x;
		const deltaY = e.clientY - dragStart.y;

		setDragOffset((prev) => ({
			x: prev.x + deltaX,
			y: prev.y + deltaY
		}));

		setDragStart({ x: e.clientX, y: e.clientY });
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	// Handle wheel zoom
	const handleWheel = (e: React.WheelEvent) => {
		e.preventDefault();
		if (e.deltaY < 0) {
			// Zoom in
			setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 5));
		} else {
			// Zoom out
			setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
		}
	};

	// Prevent modal closing when clicking the image container
	const handleModalContentClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	// Add event listeners for mouse up to handle case when user releases outside the container
	useEffect(() => {
		const handleGlobalMouseUp = () => {
			if (isDragging) {
				setIsDragging(false);
			}
		};

		window.addEventListener('mouseup', handleGlobalMouseUp);

		return () => {
			window.removeEventListener('mouseup', handleGlobalMouseUp);
		};
	}, [isDragging]);

	if (!currentRequest) {
		return null;
	}

	// Get status colors based on request status
	const getStatusStyles = (status: string) => {
		if (status === 'pending') {
			return {
				bgColor: 'rgba(246, 224, 94, 0.2)',
				textColor: '#B7791F'
			};
		} else if (status === 'dismissed') {
			return {
				bgColor: 'rgba(229, 62, 62, 0.1)',
				textColor: '#C53030'
			};
		} else {
			return {
				bgColor: 'rgba(104, 211, 145, 0.2)',
				textColor: '#2F855A'
			};
		}
	};

	const statusStyles = getStatusStyles(currentRequest.status || status);

	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: 3,
				mb: 3,
				width: isMobile ? '100%' : 800,
				mx: 'auto',
				overflow: 'visible',
				border: '1px solid rgba(0, 0, 0, 0.06)',
				boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
				transition: 'all 0.3s ease-in-out',
				'&:hover': {
					boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
					transform: 'translateY(-2px)'
				}
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					p: isMobile ? 2 : 3,
					borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
					background: 'linear-gradient(to right, rgba(30, 64, 175, 0.02), rgba(30, 64, 175, 0.08))',
					justifyContent: 'space-between'
				}}
			>
				<Box>
					<Typography
						variant="h6"
						fontWeight={700}
						sx={{ color: '#1E3A8A', fontSize: isMobile ? '1rem' : '1.25rem' }}
					>
						{currentRequest.user?.firstName} {currentRequest.user?.lastName}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ fontWeight: 500, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
						>
							Status:{' '}
						</Typography>
						<Chip
							label={currentRequest.status || status}
							size="small"
							sx={{
								bgcolor: statusStyles.bgColor,
								color: statusStyles.textColor,
								fontWeight: 600,
								fontSize: '0.75rem',
								px: 0.5,
								height: 24,
								borderRadius: 1.5,
								textTransform: 'capitalize'
							}}
						/>
					</Box>
				</Box>
				{isMobile && (
					<Button
						variant="contained"
						size="small"
						onClick={handleOpenRequest}
						sx={{
							bgcolor: '#1E40AF',
							color: 'white',
							fontWeight: 600,
							fontSize: '0.75rem',
							height: '32px',
							minWidth: '90px',
							'&:hover': {
								bgcolor: '#1E3A8A'
							}
						}}
					>
						Open
					</Button>
				)}
			</Box>

			<CardContent sx={{ p: isMobile ? 2 : 3.5 }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: isMobile ? 'column' : 'row',
						gap: 3,
						width: '100%'
					}}
				>
					{/* Left side - Request information */}
					<Box
						sx={{
							flex: isMobile ? '1 1 auto' : '0 0 45%',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							mb: isMobile ? 2 : 0
						}}
					>
						<Box>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									gap: 1,
									mb: 1,
									mr: 1,
									justifyContent: 'space-between'
								}}
							>
								<Typography
									variant="h6"
									fontWeight={700}
									sx={{ color: '#1E3A8A', mb: 0.5, fontSize: isMobile ? '0.95rem' : '1.25rem' }}
								>
									{currentRequest.name}
								</Typography>
								<Chip
									label={`Request ${currentRequestIndex + 1} of ${requests.length}`}
									size="small"
									sx={{
										bgcolor: 'rgba(30, 64, 175, 0.1)',
										color: '#1E40AF',
										fontWeight: 600,
										fontSize: '0.7rem',
										height: 20
									}}
								/>
							</Box>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
							>
								{currentRequest.description}
							</Typography>
							<Box
								sx={{
									display: 'flex',
									mt: 1,
									mr: 1,
									gap: 2,
									flexDirection: 'row',
									justifyContent: 'space-between'
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
								>
									Created: {formatDate(currentRequest.creationDateTime)}
								</Typography>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
								>
									Due: {formatDate(currentRequest.expirationDateTime)}
								</Typography>
							</Box>
						</Box>
						{!isMobile && (
							<Box sx={{ display: 'flex', mt: 1, justifyContent: 'right' }}>
								<Button
									variant="contained"
									size="medium"
									onClick={handleOpenRequest}
									sx={{
										bgcolor: '#1E40AF',
										color: 'white',
										fontWeight: 600,
										fontSize: '0.875rem',
										'&:hover': {
											bgcolor: '#1E3A8A'
										}
									}}
								>
									Open Request
								</Button>
							</Box>
						)}
					</Box>
					{/* Right side - Document preview and navigation */}
					{currentStep && (
						<Box
							sx={{
								flex: isMobile ? '1 1 auto' : '0 0 53%',
								width: '100%'
							}}
						>
							<Typography
								variant="subtitle1"
								sx={{
									mb: 1,
									fontWeight: 700,
									color: '#1E3A8A',
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									fontSize: isMobile ? '0.9rem' : '1rem',
									flexWrap: isMobile ? 'wrap' : 'nowrap'
								}}
							>
								{currentStep.name}
								{currentStep.verificationRequired && (
									<Chip
										label="Verification Required"
										size="small"
										sx={{
											bgcolor: 'rgba(246, 173, 85, 0.2)',
											color: '#C05621',
											fontWeight: 500,
											fontSize: '0.7rem',
											height: 20,
											ml: 1
										}}
									/>
								)}
								{!currentStep.optional ? (
									<Chip
										label="Required"
										size="small"
										sx={{
											bgcolor: 'rgba(229, 62, 62, 0.1)',
											color: '#C53030',
											fontWeight: 500,
											fontSize: '0.7rem',
											height: 20,
											ml: 1
										}}
									/>
								) : (
									<Chip
										label="Optional"
										size="small"
										sx={{
											bgcolor: 'rgba(102, 126, 234, 0.1)',
											color: '#5A67D8',
											fontWeight: 500,
											fontSize: '0.7rem',
											height: 20,
											ml: 1
										}}
									/>
								)}
							</Typography>

							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 2, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
							>
								{currentStep.description}
							</Typography>

							<Box
								sx={{
									border: '2px dashed rgba(30, 64, 175, 0.2)',
									borderRadius: 2,
									p: 0,
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									height: isMobile ? 180 : 220,
									position: 'relative',
									bgcolor: 'rgba(30, 64, 175, 0.02)',
									overflow: 'hidden'
								}}
							>
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										p: isMobile ? 1 : 1.5,
										bgcolor: 'rgba(255, 255, 255, 0.9)',
										borderBottom: '1px dashed rgba(30, 64, 175, 0.2)',
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										zIndex: 1
									}}
								>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 500,
											color: 'text.secondary',
											fontSize: isMobile ? '0.65rem' : '0.75rem'
										}}
									>
										Document Type:{' '}
										{sharedDocumentsMapping.find((doc) => doc.value === currentStep.docType)
											?.label || 'Not specified'}
									</Typography>
									<Chip
										label={`Step ${currentStepIndex + 1} of ${steps.length}`}
										size="small"
										sx={{
											bgcolor: 'rgba(30, 64, 175, 0.1)',
											color: '#1E40AF',
											fontWeight: 600,
											fontSize: '0.7rem',
											height: 20
										}}
									/>
								</Box>

								{typeof blobContent === 'string' && blobContent !== 'Error loading document' && (
									<Box
										sx={{
											height: '100%',
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: 'pointer',
											'&:hover': {
												opacity: 0.9
											},
											pt: 5 // Add padding top to account for the header
										}}
										onClick={handleImageClick}
									>
										<img
											src={blobContent}
											alt="Document"
											style={{
												maxHeight: '100%',
												maxWidth: '100%',
												objectFit: 'contain',
												padding: '10px'
											}}
										/>
									</Box>
								)}

								{(!blobContent || blobContent === 'Error loading document') && (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ fontStyle: 'italic', fontSize: isMobile ? '0.8rem' : '0.875rem' }}
									>
										{blobContent === 'Error loading document'
											? 'Error loading document'
											: 'No document available'}
									</Typography>
								)}
							</Box>

							{/* Step navigation */}
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									mt: 2,
									pt: 2,
									borderTop: '1px solid rgba(0, 0, 0, 0.06)'
								}}
							>
								<Button
									startIcon={<ChevronLeftIcon />}
									onClick={prevStep}
									disabled={currentStepIndex === 0}
									size={isMobile ? 'small' : 'medium'}
									sx={{
										color: '#1E40AF',
										borderRadius: 1.5,
										fontWeight: 500,
										fontSize: isMobile ? '0.7rem' : '0.75rem',
										'&:hover': {
											bgcolor: 'rgba(30, 64, 175, 0.08)'
										},
										'&.Mui-disabled': {
											color: 'rgba(0, 0, 0, 0.26)'
										}
									}}
								>
									{isMobile ? 'Prev' : 'Previous Step'}
								</Button>
								<Chip
									label={`Step ${currentStepIndex + 1} of ${steps.length}`}
									size="small"
									sx={{
										bgcolor: 'rgba(30, 64, 175, 0.08)',
										color: '#1E40AF',
										fontWeight: 600,
										fontSize: '0.7rem',
										height: 20
									}}
								/>
								<Button
									endIcon={<ChevronRightIcon />}
									onClick={nextStep}
									disabled={currentStepIndex === steps.length - 1}
									size={isMobile ? 'small' : 'medium'}
									sx={{
										color: '#1E40AF',
										borderRadius: 1.5,
										fontWeight: 500,
										fontSize: isMobile ? '0.7rem' : '0.75rem',
										'&:hover': {
											bgcolor: 'rgba(30, 64, 175, 0.08)'
										},
										'&.Mui-disabled': {
											color: 'rgba(0, 0, 0, 0.26)'
										}
									}}
								>
									{isMobile ? 'Next' : 'Next Step'}
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			</CardContent>

			<Box
				sx={{
					p: isMobile ? 2 : 3,
					pt: isMobile ? 1 : 1,
					display: 'flex',
					justifyContent: 'space-between',
					borderTop: '1px solid rgba(0, 0, 0, 0.06)',
					bgcolor: 'rgba(249, 250, 251, 0.5)'
				}}
			>
				<Button
					startIcon={<ChevronLeftIcon />}
					onClick={prevRequest}
					disabled={currentRequestIndex === 0}
					size={isMobile ? 'small' : 'medium'}
					sx={{
						color: '#1E40AF',
						borderRadius: 1.5,
						fontWeight: 500,
						fontSize: isMobile ? '0.7rem' : '0.875rem',
						'&:hover': {
							bgcolor: 'rgba(30, 64, 175, 0.08)'
						},
						'&.Mui-disabled': {
							color: 'rgba(0, 0, 0, 0.26)'
						}
					}}
				>
					{isMobile ? 'Prev' : 'Previous Request'}
				</Button>
				<Button
					endIcon={<ChevronRightIcon />}
					onClick={nextRequest}
					disabled={currentRequestIndex === requests.length - 1}
					size={isMobile ? 'small' : 'medium'}
					sx={{
						color: '#1E40AF',
						borderRadius: 1.5,
						fontWeight: 500,
						fontSize: isMobile ? '0.7rem' : '0.875rem',
						'&:hover': {
							bgcolor: 'rgba(30, 64, 175, 0.08)'
						},
						'&.Mui-disabled': {
							color: 'rgba(0, 0, 0, 0.26)'
						}
					}}
				>
					{isMobile ? 'Next' : 'Next Request'}
				</Button>
			</Box>

			{/* Image Modal */}
			<Modal
				open={imageModalOpen}
				onClose={handleCloseModal}
				aria-labelledby="image-modal"
				aria-describedby="enlarged-document-image"
				onClick={handleCloseModal}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '95vw',
						height: '95vh',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 0,
						borderRadius: 2,
						outline: 'none',
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column'
					}}
					onClick={handleModalContentClick}
				>
					{/* Toolbar */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							p: 1,
							bgcolor: '#1E40AF',
							color: 'white',
							borderTopLeftRadius: 8,
							borderTopRightRadius: 8
						}}
					>
						<Typography variant="subtitle1" fontWeight={600}>
							Document Preview
						</Typography>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<IconButton
								onClick={handleZoomReset}
								size="small"
								sx={{
									color: 'white',
									'&:hover': {
										bgcolor: 'rgba(255, 255, 255, 0.1)'
									}
								}}
							>
								<FullscreenIcon fontSize="small" />
							</IconButton>
							<IconButton
								onClick={handleCloseModal}
								size="small"
								sx={{
									color: 'white',
									'&:hover': {
										bgcolor: 'rgba(255, 255, 255, 0.1)'
									}
								}}
							>
								<CloseOutlinedIcon fontSize="small" />
							</IconButton>
						</Box>
					</Box>

					{/* Image Viewer */}
					{typeof blobContent === 'string' && blobContent !== 'Error loading document' && (
						<Box
							ref={imageContainerRef}
							sx={{
								flex: 1,
								overflow: 'hidden',
								position: 'relative',
								bgcolor: '#f5f5f5',
								cursor: isDragging ? 'grabbing' : zoomLevel > 1 ? 'grab' : 'default'
							}}
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onWheel={handleWheel}
						>
							<Box
								sx={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									transform: `translate(calc(-50% + ${dragOffset.x}px), calc(-50% + ${dragOffset.y}px)) scale(${zoomLevel})`,
									transformOrigin: 'center center',
									transition: 'transform 0.1s ease-out'
								}}
							>
								<img
									src={blobContent}
									alt="Document"
									style={{
										maxWidth: '100%',
										maxHeight: '100%',
										display: 'block',
										userSelect: 'none'
									}}
									draggable={false}
								/>
							</Box>
						</Box>
					)}

					{/* Controls */}
					<Box
						sx={{
							p: 2,
							bgcolor: 'rgba(30, 64, 175, 0.05)',
							borderTop: '1px solid rgba(30, 64, 175, 0.1)',
							display: 'flex',
							alignItems: 'center',
							gap: 2
						}}
					>
						<IconButton
							onClick={handleZoomOut}
							sx={{
								color: '#1E40AF',
								bgcolor: 'background.paper',
								boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
								'&:hover': {
									bgcolor: 'rgba(255, 255, 255, 0.9)'
								},
								'&:disabled': {
									color: 'rgba(0, 0, 0, 0.26)'
								}
							}}
							disabled={zoomLevel <= 0.5}
						>
							<ZoomOutIcon />
						</IconButton>

						<Slider
							value={zoomLevel}
							min={0.5}
							max={5}
							step={0.1}
							onChange={handleSliderChange}
							sx={{
								color: '#1E40AF',
								flex: 1,
								'& .MuiSlider-thumb': {
									height: 20,
									width: 20,
									backgroundColor: 'background.paper',
									border: '2px solid #1E40AF',
									boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
									'&:focus, &:hover, &.Mui-active': {
										boxShadow: '0 3px 8px rgba(0,0,0,0.3)'
									}
								},
								'& .MuiSlider-track': {
									height: 4
								},
								'& .MuiSlider-rail': {
									height: 4,
									opacity: 0.3
								}
							}}
							aria-labelledby="zoom-slider"
						/>

						<IconButton
							onClick={handleZoomIn}
							sx={{
								color: '#1E40AF',
								bgcolor: 'background.paper',
								boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
								'&:hover': {
									bgcolor: 'rgba(255, 255, 255, 0.9)'
								},
								'&:disabled': {
									color: 'rgba(0, 0, 0, 0.26)'
								}
							}}
							disabled={zoomLevel >= 5}
						>
							<ZoomInIcon />
						</IconButton>

						<Typography variant="body2" sx={{ fontWeight: 500, minWidth: 60, color: '#1E40AF' }}>
							{`${(zoomLevel * 100).toFixed(0)}%`}
						</Typography>
					</Box>

					{/* Help text */}
					<Typography
						variant="caption"
						sx={{
							textAlign: 'center',
							p: 1,
							bgcolor: 'rgba(249, 250, 251, 0.8)',
							color: 'text.secondary',
							borderBottomLeftRadius: 8,
							borderBottomRightRadius: 8
						}}
					>
						Use mouse wheel to zoom or drag image to pan when zoomed in
					</Typography>
				</Box>
			</Modal>
		</Card>
	);
};

export default UserDocumentRequest;
